import { CartUpdateEvent } from '@theme/events';

class CartPersonalisation extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.input = this.querySelector('[data-personalisation-input]');
    this.status = this.querySelector('[data-personalisation-status]');
    this.button = this.querySelector('[data-personalisation-save]');
    this.savedValue = this.input.value;
    this.input.addEventListener('input', () => {
      this.status.textContent = this.input.value !== this.savedValue ? 'Save your changes before checkout.' : '';
    });
    this.button.addEventListener('click', () => this.save());
  }

  get dirty() { return this.input?.value !== this.savedValue; }

  async save() {
    if (CartPersonalisation.saving) return;
    if (!this.input.reportValidity()) return;
    const value = this.input.value.trim();
    CartPersonalisation.saving = true;
    this.button.disabled = true;
    this.input.disabled = true;
    this.status.textContent = 'Saving…';
    const containers = [...document.querySelectorAll('cart-items-component')];
    containers.forEach((component) => component.classList.add('cart-items-disabled'));
    const root = window.Shopify?.routes?.root || '/';
    let result;
    let succeeded = false;
    try {
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Unable to load your cart. Please try again.');
      const cart = await cartResponse.json();
      const item = cart.items.find((item) => item.key === this.dataset.lineKey);
      if (!item) throw new Error('This item has changed. Refresh your cart and try again.');
      const properties = { ...item.properties, 'Personalised name': value };
      const sectionIds = [...new Set(containers.map((component) => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: item.quantity, properties, sections: sectionIds, sections_url: window.location.pathname }),
      });
      result = await response.json();
      if (!response.ok || result.errors) throw new Error(result.description || 'Unable to save your name. Please try again.');
      succeeded = true;
      this.savedValue = value;
      this.input.value = value;
      this.status.textContent = value ? 'Personalised name saved.' : 'Personalisation removed.';
    } catch (error) {
      this.status.textContent = error.message;
    } finally {
      CartPersonalisation.saving = false;
      this.button.disabled = false;
      this.input.disabled = false;
      containers.forEach((component) => component.classList.remove('cart-items-disabled'));
    }
    if (succeeded) {
      document.dispatchEvent(new CartUpdateEvent(result, 'cart-personalisation', {
        itemCount: result.item_count,
        source: 'cart-personalisation',
        sections: result.sections,
      }));
    }
  }
}

if (!customElements.get('cart-personalisation')) {
  customElements.define('cart-personalisation', CartPersonalisation);
  // Prevent leaving for checkout with a pending or unsaved personalised name.
  const guardCheckout = (event) => {
    const checkout = event.target.closest?.('[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons');
    if (!checkout && !(event.type === 'submit' && event.submitter?.name === 'checkout')) return;
    const pending = [...document.querySelectorAll('cart-personalisation')].find((field) => field.dirty);
    if (!pending && !CartPersonalisation.saving) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending) {
      pending.querySelector('details').open = true;
      pending.status.textContent = 'Please save your personalised name before checkout.';
      pending.input.focus();
    }
  };
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
}
